import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './auth'
import { useProductName, applyServerProductName } from './productName'
import { applyServerNavColor } from './navColor'
import { applyServerNavBanner } from './navBanner'
import { initUiConfigSync, applyServerTheme, applyUploadedFonts } from './uiConfig'
import Layout from './components/Layout'
import RequireAdmin from './components/RequireAdmin'
import StudioLayout from './components/StudioLayout'
import Login from './pages/Login'
import ContractChat from './pages/ContractChat'
import ITReview from './pages/ITReview'
import ContractSubmissions from './pages/ContractSubmissions'
import Workspace from './pages/Workspace'
import WorkProduct from './pages/WorkProduct'
import Stats from './pages/Stats'
import Settings from './pages/Settings'
import Privacy from './pages/Privacy'
// Admin surfaces — only reachable under /studio/* (or /artifact/:id deep link)
import PipelinePage from './pages/PipelinePage'
import PromptsPage from './pages/PromptsPage'
import Observability from './pages/Observability'
import Searches from './pages/Searches'
import QA from './pages/QA'
import ControlB from './pages/ControlB'
import StudioIndex from './pages/studio/Index'
import TestSets from './pages/studio/TestSets'
import PipelineTest from './pages/studio/PipelineTest'
import AIConfig from './pages/studio/AIConfig'
import ClaudeTab from './pages/studio/ClaudeTab'
import SyncTab from './pages/studio/SyncTab'
import ArtifactViewer from './pages/ArtifactViewer'
import Artifacts from './pages/Artifacts'
import ImportV1 from './pages/ImportV1'
import AccountingPage from './pages/AccountingPage'
import ComingSoon from './pages/ComingSoon'

export default function App() {
  const auth = useAuth()
  // One-time cleanup of legacy `me_chat_history::*` localStorage keys —
  // pre-task-X.A the chat panel persisted message arrays per scope into
  // localStorage. We're fully server-side now (apps/chat/models.py +
  // /api/chat/history/), but old keys linger on returning users'
  // browsers. They're not read anywhere current; just wipe them so
  // they don't show up in DevTools and confuse anyone (including us)
  // about whether chat is genuinely browser-stored.
  useEffect(() => {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i)
        if (k && k.startsWith('me_chat_history::')) localStorage.removeItem(k)
      }
    } catch { /* ignore — Safari private mode etc. */ }
  }, [])
  // Track the most recent non-artifact-viewer SPA location so the
  // ArtifactViewer's back button can return to the exact list view
  // (including ?filter=, ?q=, ?sort= etc.) the user was on. document.referrer
  // only reflects full page loads, not in-app navigation, so it's
  // unreliable on its own — clicking from /studio/artifacts after having
  // loaded the tab on /studio/observability would still report observability.
  const location = useLocation()
  const product = useProductName()
  useEffect(() => {
    const path = location.pathname + location.search
    // Skip artifact-viewer paths (so the viewer's back button doesn't loop
    // to itself) AND /login (a transitional gateway, never a real "prior
    // page" — caused contamination after auth-expiry redirect chains
    // landing back on /workspace via the wildcard).
    if (path.startsWith('/artifact/')
        || path.startsWith('/studio/artifact/')
        || path === '/login') return
    try { sessionStorage.setItem('me_last_browser_location', path) } catch { /* ignore */ }
  }, [location])
  // When the user becomes unauthenticated (manual logout, expired
  // session, fresh tab), wipe the back-button breadcrumb so the next
  // user (or same user logging back in) doesn't inherit the previous
  // session's last location. SessionStorage is per-tab, so this only
  // affects this tab — other open tabs keep their own state.
  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      try { sessionStorage.removeItem('me_last_browser_location') } catch { /* ignore */ }
    }
  }, [auth.status])
  // Per-section browser tab title. Reflects which page the user has
  // open so multiple tabs can be told apart at a glance. The favicon
  // (a lightning bolt) renders on the tab regardless.
  useEffect(() => {
    const path = location.pathname
    const section = (() => {
      // Contract Checklist tab labels (Ken 2026-09-29): worker-facing chat vs memo.
      if (path.startsWith('/contract/')) return 'Kai'
      if (path.startsWith('/studio/contract-submissions')) return 'MemoCC'
      // Client-facing pages use their QA-editable names (Ken 2026-08-11).
      if (path.startsWith('/workspace')) return product.pageMainName
      if (path.startsWith('/stats')) return product.pageStatsName
      if (path.startsWith('/work-product')) return product.pagePlaybookName
      if (path.startsWith('/settings')) return 'Settings'
      if (path.startsWith('/privacy')) return 'Privacy'
      if (path === '/' || path === '/login') return 'Matter Engine'
      if (path.startsWith('/studio/artifacts')) return 'Artifacts'
      if (path.startsWith('/studio/artifact/') || path.startsWith('/artifact/')) return 'Artifact'
      if (path.startsWith('/studio/pipeline-test')) return 'Pipeline Test'
      if (path.startsWith('/studio/pipeline')) return 'Pipeline'
      if (path.startsWith('/studio/prompts')) return 'Prompts'
      if (path.startsWith('/studio/test-sets')) return 'Test Sets'
      if (path.startsWith('/studio/observability')) return 'Observability'
      if (path.startsWith('/studio/qa')) return 'QA'
      if (path.startsWith('/studio/imports')) return 'Imports'
      if (path.startsWith('/studio/ai-config')) return 'AI Config'
      if (path.startsWith('/studio/claude')) return 'AI'
      if (path.startsWith('/studio/sync')) return 'Sync'
      if (path.startsWith('/studio/searches')) return 'Search'
      if (path.startsWith('/studio')) return 'Studio'
      return 'Matter Engine'
    })()
    document.title = section
  }, [location.pathname, product.pageMainName, product.pageStatsName, product.pagePlaybookName])
  // Load the tenant's server-side UI config (wordmark / fonts / nav colour)
  // and apply it, so every user of the tenant sees what the brander set on the
  // QA page — not just the browser that set it. Server wins over localStorage;
  // on any failure we silently keep localStorage/defaults. Re-runs when the
  // logged-in (or impersonated) user changes.
  const authedUser = auth.status === 'authenticated' ? auth.user : null
  useEffect(() => {
    if (!authedUser) return
    let cancelled = false
    initUiConfigSync(authedUser).then((cfg) => {
      if (cancelled || !cfg) return
      if (cfg.product_name) applyServerProductName(cfg.product_name)
      if (cfg.nav_color) applyServerNavColor(cfg.nav_color)
      applyServerNavBanner(cfg.nav_banner)
      // Per-tenant theme (Ken 2026-10-22). Always call — passing the (possibly
      // absent) theme also CLEARS any prior tenant's overrides on an act-as switch.
      applyServerTheme(cfg.theme)
      // Uploaded custom fonts (Ken 2026-10-27). Always call so a tenant without
      // any (MOVU) or an act-as switch de-registers a prior tenant's @font-face.
      applyUploadedFonts(cfg.fonts)
    }).catch(() => { /* keep localStorage / defaults */ })
    return () => { cancelled = true }
  }, [authedUser?.id])
  // Public passwordless contract-checklist chat — renders in ANY auth state
  // (submitters have no login), so it must short-circuit before the auth gates.
  if (location.pathname.startsWith('/contract/')) {
    return (
      <Routes>
        <Route path="/contract/:token" element={<ContractChat />} />
      </Routes>
    )
  }
  // Public passwordless IT-reviewer page — scoped to one agreement, no login, no matters.
  if (location.pathname.startsWith('/it-review/')) {
    return (
      <Routes>
        <Route path="/it-review/:token" element={<ITReview />} />
      </Routes>
    )
  }
  if (auth.status === 'loading') {
    // Circular spinner — same pattern used in Artifacts / Workspace /
    // MatterTimelinePane for consistency. Shown on every cold load
    // (including /login, where it bridges the gap between blank page
    // and Login form fading in).
    return (
      <div className="grid h-full place-items-center">
        <div className="size-8 rounded-full border-[3px] border-[color:var(--me-textMute)] border-t-transparent animate-spin" />
      </div>
    )
  }
  if (auth.status === 'unauthenticated') {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }
  return (
    <>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to="/workspace" replace />} />
          <Route path="/workspace" element={<Workspace />} />
          <Route path="/work-product" element={<WorkProduct />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* Accounting "Page" — superuser-only (guarded in-component + API). */}
          <Route path="/accounting" element={<AccountingPage />} />
          {/* wip subpages not yet built — blank "coming soon" stubs (Ken 2026-10-22). */}
          <Route path="/internal-outreach" element={<ComingSoon title="Internal Outreach" />} />
          <Route path="/org-chart" element={<ComingSoon title="Org Chart" />} />
          <Route path="/break-room" element={<ComingSoon title="Breaks" />} />
          <Route path="/artifact/:id" element={<ArtifactViewer />} />
          {/* Old top-level admin URLs — preserved as redirects so existing
              bookmarks / docs / external links don't 404 after the
              consolidation under /studio. */}
          <Route path="/artifacts" element={<Navigate to="/studio/artifacts" replace />} />
          <Route path="/pipeline" element={<Navigate to="/studio/pipeline" replace />} />
          <Route path="/prompts" element={<Navigate to="/studio/prompts" replace />} />
          <Route path="/observability" element={<Navigate to="/studio/observability" replace />} />
          <Route path="/imports/v1" element={<Navigate to="/studio/imports" replace />} />
        </Route>
        <Route path="/studio" element={<RequireAdmin><StudioLayout /></RequireAdmin>}>
          <Route index element={<StudioIndex />} />
          <Route path="artifacts" element={<Artifacts />} />
          {/* Studio-flavored artifact viewer: same component, different
              parent layout (StudioLayout's nav). Linked from /studio/artifacts
              and other Studio-internal flows. End users without Studio
              access can't reach this URL — RequireAdmin gates the entire
              /studio/* tree, so the workspace remains the only artifact
              viewer for them. */}
          <Route path="artifact/:id" element={<ArtifactViewer />} />
          <Route path="pipeline" element={<PipelinePage />} />
          <Route path="pipeline-test" element={<PipelineTest />} />
          <Route path="prompts" element={<PromptsPage />} />
          <Route path="test-sets" element={<TestSets />} />
          <Route path="observability" element={<Observability />} />
          <Route path="searches" element={<Searches />} />
          <Route path="qa" element={<QA />} />
          <Route path="contract-submissions" element={<ContractSubmissions />} />
          <Route path="control-b" element={<ControlB />} />
          <Route path="imports" element={<ImportV1 />} />
          <Route path="ai-config" element={<AIConfig />} />
          <Route path="claude" element={<ClaudeTab />} />
          <Route path="sync" element={<SyncTab />} />
          <Route path="claude/:chatId" element={<ClaudeTab />} />
        </Route>
        <Route path="*" element={<Navigate to="/workspace" replace />} />
      </Routes>
    </>
  )
}
